/**
 * 🔴 #2156 — QUAND un intercepteur HTTP a-t-il le droit de rejouer une requête ?
 *
 * Constat de la recette d'acceptance du 28/10 (test 12) : un refus métier de
 * `createInvoice` (`400 TOTAL_BELOW_MINIMUM`) partait QUATRE fois pour un clic,
 * et l'écran n'en recevait jamais le code. Deux défauts, dans les deux
 * intercepteurs enregistrés par `CoreModule` :
 *   - `SecurityInterceptor` rejouait TOUTE erreur, 4xx compris, et quelle que
 *     soit la méthode : un refus métier était rejoué deux fois ;
 *   - `CorsInterceptor` rejouait les erreurs réseau et 502 à 504 de n'importe
 *     quelle méthode. Pour `createInvoice`, un POST qui crée une session Stripe
 *     et un `pendingShipments` sans clé d'idempotence, une réponse perdue
 *     rejouée = une seconde session payable (relecture `payment-flow-guardian`).
 *
 * La règle, unique pour les deux intercepteurs : on ne rejoue QUE
 *   1. une méthode IDEMPOTENTE (`GET`, `HEAD`, `OPTIONS`), ou un POST de la liste
 *      `READ_ONLY_POST_FUNCTIONS` — jamais un autre POST, qui peut avoir abouti
 *      côté serveur alors que sa réponse s'est perdue ;
 *   2. sur une panne PASSAGÈRE : réseau coupé (statut 0), 502, 503, 504 ou délai
 *      dépassé. Un 4xx est une réponse du serveur : la rejouer donne la même
 *      réponse, en plus cher.
 */
import { HttpRequest } from '@angular/common/http';

const IDEMPOTENT_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);

/**
 * 🔴 #2156 (arbitrage de Yannick Kalemba, 29/10) — les Cloud Functions appelées
 * en POST qui ne font QUE LIRE : recherche d'adresse HERE, points relais DPD,
 * géocodage. Elles ne doivent pas perdre leur rejeu sur une panne passagère.
 *
 * Vérifié dans `functions/src` au 29/10 : aucune n'écrit en base. Une fonction
 * n'entre ici que si c'est vrai, et qu'elle le reste : jamais `createInvoice`,
 * ni rien qui crée, envoie ou débite. Identifiées par leur NOM, le dernier
 * segment de l'URL, stable entre `cloudfunctions.net` et le proxy local `/api`.
 */
export const READ_ONLY_POST_FUNCTIONS: ReadonlySet<string> = new Set([
  'getHereCountries',
  'getHereCities',
  'getHerePostalCodes',
  'getHereStreets',
  'getHereAddresses',
  'reverseGeocode',
  'geocodeAddress',
  'getPickupPoints',
]);

/** Nom de la fonction visée : le dernier segment du chemin, sans requête. */
function functionNameOf(url: string): string {
  const path = url.split('?')[0].split('#')[0].replace(/\/+$/, '');
  return path.substring(path.lastIndexOf('/') + 1);
}

/** Un POST vers une fonction de lecture seule de la liste. */
export function isReadOnlyPost(req: HttpRequest<unknown>): boolean {
  return req.method.toUpperCase() === 'POST' && READ_ONLY_POST_FUNCTIONS.has(functionNameOf(req.url));
}

const TRANSIENT_STATUSES = new Set([0, 502, 503, 504]);

/** Une panne qui peut disparaître d'elle-même : réseau, passerelle, délai. */
export function isTransientHttpError(error: unknown): boolean {
  const e = error as { status?: unknown; name?: unknown } | null;
  if (!e) return false;
  if (e.name === 'TimeoutError') return true;
  return typeof e.status === 'number' && TRANSIENT_STATUSES.has(e.status);
}

/** `true` seulement si rejouer `req` après `error` ne peut rien doubler. */
export function mayRetryRequest(req: HttpRequest<unknown>, error: unknown): boolean {
  const replayable = IDEMPOTENT_METHODS.has(req.method.toUpperCase()) || isReadOnlyPost(req);
  return replayable && isTransientHttpError(error);
}
